import React, { useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Fade,
  Stack,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoIcon from '@mui/icons-material/Info';
import DomainIcon from '@mui/icons-material/Domain';
import EmailIcon from '@mui/icons-material/Email';
import ContactsIcon from '@mui/icons-material/Contacts';
import TemplateIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import LaunchIcon from '@mui/icons-material/Launch';
import InfoDialog from '../InfoDialog';
import { Card, CardContent, Button, Chip } from '../ui';
import {
  acme,
  acmeDashboardPanelSx,
  acmeDashboardSectionTitleSx,
  acmeDashboardSectionDescSx,
  acmeInfoCalloutSx,
  acmeInfoCardActiveSx,
} from '../../theme/acmeTheme';

const OnboardingProgress = ({ onboardingData, refreshOnboardingData }) => {
  const navigate = useNavigate();
  const [infoDialog, setInfoDialog] = useState({ open: false, stepId: '', stepTitle: '' });

  const steps = [
    {
      id: 'domains',
      title: 'Add Domains',
      description:
        'Set up your email domains for better deliverability and authentication. Configure SPF, DKIM, and DMARC records.',
      icon: DomainIcon,
      path: '/domains',
      completed: onboardingData.domains,
      quickTip: 'Add your business domain to start sending professional emails',
    },
    {
      id: 'emails',
      title: 'Add Working Emails',
      description:
        'Configure and verify your sender email addresses. Upload avatars and set up your email signatures.',
      icon: EmailIcon,
      path: '/emails',
      completed: onboardingData.emails,
      quickTip: 'Verify at least one email address to start sending campaigns',
    },
    {
      id: 'contacts',
      title: 'Add Contacts',
      description:
        'Import your contact lists and organize them into groups. Build your subscriber base for targeted campaigns.',
      icon: ContactsIcon,
      path: '/contacts',
      completed: onboardingData.contacts,
      quickTip: 'Import your contacts or create contact groups for better targeting',
    },
    {
      id: 'templates',
      title: 'Add Templates',
      description:
        'Create beautiful email templates or choose from our collection. Design responsive emails that look great on all devices.',
      icon: TemplateIcon,
      path: '/templates',
      completed: onboardingData.templates,
      quickTip: 'Create your first template or browse our template library',
    },
    {
      id: 'campaigns',
      title: 'Create Campaign',
      description:
        'Launch your first email campaign! Send targeted messages to your contacts and track engagement metrics.',
      icon: CampaignIcon,
      path: '/campaigns',
      completed: onboardingData.campaigns,
      quickTip: 'Create your first campaign to start engaging with your audience',
    },
  ];

  const completedSteps = steps.filter((step) => step.completed).length;
  const progressPercentage = (completedSteps / steps.length) * 100;
  const allComplete = completedSteps === steps.length;

  const handleStepClick = (path) => {
    navigate(path);
  };

  const handleInfoClick = (stepId, stepTitle) => {
    setInfoDialog({ open: true, stepId, stepTitle });
  };

  const handleInfoClose = () => {
    setInfoDialog({ open: false, stepId: '', stepTitle: '' });
  };

  return (
    <Box
      sx={{
        ...acmeDashboardPanelSx,
        p: { xs: 2, sm: 3 },
        mb: 2,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography sx={{ ...acmeDashboardSectionTitleSx, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Get started with Mailsball
        </Typography>
        <Typography
          sx={{
            ...acmeDashboardSectionDescSx,
            maxWidth: 560,
            mx: 'auto',
            mb: 2,
          }}
        >
          Complete these essential steps to unlock the full potential of your email marketing platform.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: acme.colors.foreground }}>
            {completedSteps} of {steps.length} completed
          </Typography>
          <Chip
            label={`${Math.round(progressPercentage)}% complete`}
            size="small"
            sx={{
              backgroundColor: acme.colors.brand,
              color: acme.colors.brandForeground,
              fontWeight: 600,
              border: 'none',
            }}
          />
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: acme.radius.full,
            bgcolor: acme.colors.muted,
            maxWidth: 480,
            mx: 'auto',
            '& .MuiLinearProgress-bar': {
              backgroundColor: acme.colors.brand,
              borderRadius: acme.radius.full,
            },
          }}
        />
      </Box>

      <Stack spacing={0}>
        {steps.map((step, index) => {
          const StepIcon = step.icon;

          return (
            <Fade in timeout={300 * (index + 1)} key={step.id}>
              <Box>
                <Card
                  sx={{
                    boxShadow: 'none',
                    border: `1px solid ${step.completed ? acme.colors.brand : acme.colors.border}`,
                    ...(step.completed ? acmeInfoCardActiveSx('plan') : {}),
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      boxShadow: acme.shadow.lg,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: step.completed ? acme.colors.brand : acme.colors.muted,
                          color: step.completed ? acme.colors.brandForeground : acme.colors.iconDefault,
                          flexShrink: 0,
                        }}
                      >
                        {step.completed ? (
                          <CheckCircleIcon sx={{ fontSize: '1.5rem' }} />
                        ) : (
                          <StepIcon sx={{ fontSize: '1.5rem' }} />
                        )}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: acme.colors.foreground }}
                          >
                            {step.title}
                          </Typography>
                          {step.completed && (
                            <Chip
                              label="Complete"
                              size="small"
                              sx={{
                                backgroundColor: acme.colors.brandMuted,
                                color: acme.colors.brand,
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: acme.colors.mutedForegroundSolid, mb: 0.75, lineHeight: 1.5 }}
                        >
                          {step.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: acme.colors.brand, fontStyle: 'italic' }}
                        >
                          {step.quickTip}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexShrink: 0,
                          width: { xs: '100%', md: 'auto' },
                          flexDirection: { xs: 'column', sm: 'row' },
                        }}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleInfoClick(step.id, step.title)}
                          startIcon={<InfoIcon />}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          View info
                        </Button>
                        <Button
                          variant={step.completed ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => handleStepClick(step.path)}
                          endIcon={<LaunchIcon />}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          {step.completed ? 'Review' : 'Set up'}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 28,
                      color: acme.colors.mutedForegroundSolid,
                    }}
                  >
                    <ArrowDownwardIcon sx={{ fontSize: '1.25rem', opacity: 0.5 }} />
                  </Box>
                )}
              </Box>
            </Fade>
          );
        })}
      </Stack>

      {allComplete && (
        <Fade in timeout={500}>
          <Box
            sx={{
              ...acmeInfoCalloutSx,
              textAlign: 'center',
              mt: 3,
              borderColor: acme.colors.brand,
              backgroundColor: acme.colors.brandMuted,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: acme.colors.foreground, mb: 1 }}>
              Setup complete
            </Typography>
            <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}>
              You&apos;ve completed onboarding. Your dashboard now shows full statistics and analytics.
            </Typography>
            <Button variant="default" onClick={refreshOnboardingData}>
              View dashboard statistics
            </Button>
          </Box>
        </Fade>
      )}

      <InfoDialog
        open={infoDialog.open}
        onClose={handleInfoClose}
        stepId={infoDialog.stepId}
        stepTitle={infoDialog.stepTitle}
      />
    </Box>
  );
};

export default OnboardingProgress;
